import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  Stack,
  Button,
  IconButton,
} from "@mui/material";
import {
  LocalHospital,
  CalendarToday,
  Person,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";

export interface AnimalHealthRecord {
  _id: string;
  cattle_tag: string;
  data: string;
  tipo: string;
  detalhes: {
    disease?: string;
    vaccine_name?: string;
    dosage_ml?: string | number;
    route?: string;
  };
  performed_by: string;
  notes: string;
  cost: number | null;
}

export interface AnimalSanityData {
  cattle_tag: string;
  entity_type: string;
  total_encontrado: number;
  historico: AnimalHealthRecord[];
  search_criteria: {
    date_filter: string | null;
    type_filter: string;
  };
  treatment_statistics: {
    [key: string]: number;
  };
}

export interface AnimalHealthData {
  _id: string;
  name: string;
  breed_code: string;
  gender: string;
  birth_date: string;
  current_status?: string | null;
  weight: number | null;
  category: string | null;
  mother_id: string | null;
  father_id: string | null;
  location: string | null;
  registration_date: string | null;
  health_status: string | null;
  entity_type: string;
  sanity_data: AnimalSanityData;
}

export interface AnimalHealthHistoryData {
  data: AnimalHealthData;
  content_type: string;
  operation_status: string;
  profile_found: boolean;
  resumo?: string;
  url?: string;
}

export interface AnimalHealthHistoryViewProps {
  animalHealthHistoryData: AnimalHealthHistoryData;
}

const formatDate = (dateString?: string): string => {
  if (!dateString) return "Data não disponível";
  try {
    // Se a data está no formato YYYY-MM-DD, evita problemas de timezone
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    // Para outros formatos, usa a conversão normal
    return new Date(dateString).toLocaleDateString("pt-BR");
  } catch {
    return dateString;
  }
};

const getStatusColor = (status?: string | null) => {
  if (!status) {
    return DESIGN_TOKENS.colors.status.info[500];
  }
  
  switch (status.toLowerCase()) {
    case 'ativo':
    case 'active':
      return DESIGN_TOKENS.colors.brand.primary;
    case 'inativo':
    case 'inactive':
      return DESIGN_TOKENS.colors.status.error[500];
    default:
      return DESIGN_TOKENS.colors.status.info[500];
  }
};

const getTreatmentTypeLabel = (tipo: string, t: any) => {
  const treatmentKey = `health_history.treatment_types.${tipo}`;
  return t(treatmentKey, { defaultValue: tipo });
};

const getTreatmentTypeColor = (tipo: string) => {
  return '#424242'; // Cinza escuro para todos os tipos
};

const calculateAge = (birthDate: string, t: any): string => {
  if (!birthDate) return t('health_history.age_not_available');
  try {
    const birth = new Date(birthDate);
    const today = new Date();
    const ageInYears = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      return t('health_history.age_years', { years: ageInYears - 1 });
    }
    return t('health_history.age_years', { years: ageInYears });
  } catch {
    return t('health_history.age_not_available');
  }
};

export const AnimalHealthHistoryView: React.FC<AnimalHealthHistoryViewProps> = ({ animalHealthHistoryData }) => {
  const { t } = useTranslation('animal');
  const { data: animalData } = animalHealthHistoryData;
  
  // Paginação (declared before any early return to satisfy rules of hooks)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  
  // Verificações de segurança
  if (!animalData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          {t('health_history.data_not_available')}
        </Typography>
      </Box>
    );
  }
  
  // Dados seguros
  const sanityData = animalData.sanity_data || {};
  const records = sanityData.historico || [];
  const treatmentStats = sanityData.treatment_statistics || {};
  const totalRecords = sanityData.total_encontrado || records.length;
  
  const totalPages = Math.ceil(records.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentRecords = records.slice(startIndex, startIndex + itemsPerPage);
  
  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="body1"
          sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
        >
          {animalHealthHistoryData.resumo || t('health_history.records_found', { count: totalRecords })}
        </Typography>
      </Box>

      {/* Informações do Animal */}
      <Card sx={{ mb: 3, bgcolor: DESIGN_TOKENS.colors.background.elevated }}>
        <CardContent>
          <Stack direction="row" spacing={4} alignItems="center" flexWrap="wrap">
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('health_history.animal_tag')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {animalData.name}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('health_history.breed')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {animalData.breed_code || t('health_history.not_specified')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('health_history.gender')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {animalData.gender || t('health_history.not_specified_gender')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('health_history.age')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {calculateAge(animalData.birth_date, t)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('health_history.status')}
              </Typography>
              <Chip
                label={animalData.current_status || t('health_history.not_defined')}
                size="small"
                sx={{
                  bgcolor: getStatusColor(animalData.current_status),
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Estatísticas de Tratamentos */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: DESIGN_TOKENS.colors.text.primary,
              mb: 2,
            }}
          >
            {t('health_history.treatment_statistics')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Object.entries(treatmentStats).map(([tipo, count]) => (
              <Chip
                key={tipo}
                label={`${getTreatmentTypeLabel(tipo, t)}: ${count}`}
                sx={{
                  bgcolor: getTreatmentTypeColor(tipo),
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Detalhes do Animal */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: DESIGN_TOKENS.colors.text.primary,
              mb: 2,
            }}
          >
            {t('health_history.animal_information')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ minWidth: 200 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {t('health_history.birth_date')}
              </Typography>
              <Typography variant="body1">
                {formatDate(animalData.birth_date) || t('health_history.date_not_available')}
              </Typography>
            </Box>
            {animalData.weight && (
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('health_history.weight')}
                </Typography>
                <Typography variant="body1">
                  {animalData.weight} kg
                </Typography>
              </Box>
            )}
            {animalData.location && (
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('health_history.location')}
                </Typography>
                <Typography variant="body1">
                  {animalData.location}
                </Typography>
              </Box>
            )}
            {animalData.registration_date && (
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('health_history.registration_date')}
                </Typography>
                <Typography variant="body1">
                  {formatDate(animalData.registration_date)}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Histórico de Registros */}
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: DESIGN_TOKENS.colors.text.primary,
              mb: 2,
            }}
          >
            {t('health_history.health_history_title', { count: totalRecords })}
          </Typography>
          
          {currentRecords.length > 0 ? (
            <>
              <Stack spacing={2}>
                {currentRecords.map((record, index) => (
                  <Card key={index} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar
                            sx={{
                              bgcolor: getTreatmentTypeColor(record.tipo),
                              width: 32,
                              height: 32,
                            }}
                          >
                            <LocalHospital sx={{ fontSize: 18 }} />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {getTreatmentTypeLabel(record.tipo, t)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {record._id}
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          label={formatDate(record.data)}
                          size="small"
                          variant="outlined"
                        />
                      </Box>

                      <Stack spacing={1}>
                        {record.detalhes.disease && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {t('health_history.disease')}
                            </Typography>
                            <Typography variant="body2">
                              {record.detalhes.disease}
                            </Typography>
                          </Box>
                        )}

                        {record.detalhes.vaccine_name && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {t('health_history.vaccine')}
                            </Typography>
                            <Typography variant="body2">
                              {record.detalhes.vaccine_name}
                            </Typography>
                          </Box>
                        )}

                        {record.detalhes.dosage_ml && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {t('health_history.dosage')}
                            </Typography>
                            <Typography variant="body2">
                              {typeof record.detalhes.dosage_ml === 'number' 
                                ? `${record.detalhes.dosage_ml} ml` 
                                : record.detalhes.dosage_ml}
                            </Typography>
                          </Box>
                        )}

                        {record.detalhes.route && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {t('health_history.administration_route')}
                            </Typography>
                            <Typography variant="body2">
                              {record.detalhes.route}
                            </Typography>
                          </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.text.secondary }} />
                          <Typography variant="body2">
                            <strong>{t('health_history.performed_by')}</strong> {record.performed_by}
                          </Typography>
                        </Box>

                        {record.cost && (
                          <Typography variant="body2">
                            <strong>{t('health_history.cost')}</strong> R$ {record.cost.toFixed(2)}
                          </Typography>
                        )}

                        {record.notes && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {t('health_history.observations')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                              {record.notes}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>

              {/* Paginação */}
              {totalPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mt: 3,
                    gap: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<NavigateBefore />}
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                  >
                    {t('health_history.previous')}
                  </Button>
                  <Typography variant="body2" sx={{ mx: 2 }}>
                    {t('health_history.page_info', { current: currentPage, total: totalPages })}
                  </Typography>
                  <Button
                    variant="outlined"
                    endIcon={<NavigateNext />}
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  >
                    {t('health_history.next')}
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <LocalHospital
                sx={{
                  fontSize: 64,
                  color: DESIGN_TOKENS.colors.neutral[300],
                  mb: 2,
                }}
              />
              <Typography
                variant="h6"
                sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1 }}
              >
                {t('health_history.no_records_found')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
              >
                {t('health_history.no_health_records')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AnimalHealthHistoryView;